import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, CheckCircle, XCircle, RefreshCw, Search, Trash2,
  ChevronDown, ChevronRight, Activity, Loader2, ShieldCheck
} from 'lucide-react';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

/**
 * 2026-08-13 (kullanici direktifi): Teshis paneli — MENUDE GORUNMEZ.
 * Sadece adres cubuguna /#/system-errors yazarak erisilir.
 * Amac: tester "json hatasi aliyorum" dediginde gercek kok nedeni gormek.
 */

interface ErrorRow {
  id: number; referenceNo: string; occurredAt: string; source: string;
  statusCode?: number; method?: string; path?: string; queryString?: string;
  userName?: string; message?: string; hint?: string;
  exceptionType?: string; stackTrace?: string; userAgent?: string;
  clientIp?: string; isResolved: boolean;
}

interface HealthCheck { name: string; status: 'OK' | 'WARN' | 'FAIL'; detail: string; fix?: string }

const SystemErrors: React.FC = () => {
  const [rows, setRows] = useState<ErrorRow[]>([]);
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyUnresolved, setOnlyUnresolved] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const unwrap = (r: any) => { const raw = r?.data ?? r; return Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []); };

  // 2026-08-14: Backend Dapper satirlarini PascalCase (ReferenceNo, OccurredAt...) donduruyor,
  // arayuz camelCase bekliyordu -> tum alanlar bos ve "Invalid Date" gorunuyordu.
  // Her iki yazimi da kabul edecek sekilde normalize edilir.
  const al = (o: any, ...adlar: string[]) => {
    for (const a of adlar) {
      if (o?.[a] !== undefined && o?.[a] !== null) return o[a];
      const buyuk = a.charAt(0).toUpperCase() + a.slice(1);
      if (o?.[buyuk] !== undefined && o?.[buyuk] !== null) return o[buyuk];
    }
    return undefined;
  };

  const normalize = (o: any): ErrorRow => ({
    id: al(o, 'id') ?? 0,
    referenceNo: al(o, 'referenceNo') ?? '—',
    occurredAt: al(o, 'occurredAt') ?? '',
    source: al(o, 'source') ?? '—',
    statusCode: al(o, 'statusCode'),
    method: al(o, 'method'),
    path: al(o, 'path'),
    queryString: al(o, 'queryString'),
    userName: al(o, 'userName'),
    message: al(o, 'message'),
    hint: al(o, 'hint'),
    exceptionType: al(o, 'exceptionType'),
    stackTrace: al(o, 'stackTrace'),
    userAgent: al(o, 'userAgent'),
    clientIp: al(o, 'clientIp'),
    isResolved: !!al(o, 'isResolved'),
  });

  const tarihYaz = (s?: string) => {
    if (!s) return '—';
    const d = new Date(s);
    return isNaN(d.getTime()) ? String(s) : d.toLocaleString('tr-TR');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = `?limit=200${onlyUnresolved ? '&onlyUnresolved=true' : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
      const r: any = await api.get(`/system/errors${q}`);
      setRows(unwrap(r).map(normalize));
    } catch (e: any) {
      uyar('Hata kayitlari alinamadi: ' + (e?.userHint || e?.message));
    } finally { setLoading(false); }
  }, [search, onlyUnresolved]);

  const loadHealth = useCallback(async () => {
    try {
      const r: any = await api.get('/system/health');
      setHealth(r?.data ?? r);
    } catch { /* yoksay */ }
  }, []);

  useEffect(() => { load(); loadHealth(); }, []);

  const resolve = async (id: number) => {
    try { await api.post(`/system/errors/${id}/resolve`); load(); } catch { }
  };

  // 2026-08-13: Onceden hata sessizce yutuluyordu (bos catch) — kullanici "calismiyor" dedi.
  const clearOld = async (allRecords = false) => {
    const soru = allRecords
      ? 'TUM hata kayitlari silinsin mi? (geri alinamaz)'
      : '7 gunden eski hata kayitlari silinsin mi?';
    if (!(await onayla(soru))) return;
    try {
      const r: any = await api.delete(allRecords ? '/system/errors' : '/system/errors?olderThanDays=7');
      const env = r?.data ?? r;
      const silinen = env?.data?.deleted ?? env?.deleted;
      uyar(env?.message || (silinen != null ? `${silinen} kayit silindi` : 'Silme islemi tamamlandi'));
      await load();
      await loadHealth();
    } catch (e: any) {
      uyar('Silme basarisiz: ' + (e?.userHint || e?.response?.data?.message || e?.message || 'bilinmeyen hata'));
    }
  };

  const statusColor = (s?: number) => {
    if (!s) return 'bg-slate-100 text-slate-700';
    if (s >= 500) return 'bg-red-100 text-red-700';
    if (s === 405) return 'bg-purple-100 text-purple-700';
    if (s >= 400) return 'bg-amber-100 text-amber-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  const badge = (st: string) =>
    st === 'OK' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
      : st === 'WARN' ? 'bg-amber-100 text-amber-700 border-amber-200'
        : 'bg-red-100 text-red-700 border-red-200';

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <Activity size={26} className="text-red-600" /> Sistem Teşhis Paneli
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Sunucu ve tarayıcı hataları · Bu sayfa menüde görünmez, yalnızca adresle erişilir
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { load(); loadHealth(); }} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Yenile
          </button>
          <button onClick={() => clearOld(false)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-amber-200 text-amber-700 rounded-xl text-sm font-bold bg-white hover:bg-amber-50">
            <Trash2 size={16} /> 7 Günden Eskileri Sil
          </button>
          <button onClick={() => clearOld(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-xl text-sm font-bold bg-white hover:bg-red-50">
            <Trash2 size={16} /> Tümünü Sil
          </button>
        </div>
      </div>

      {/* SISTEM SAGLIK KONTROLU */}
      {health && (
        <div className={`border rounded-2xl p-5 ${
          health.overall === 'OK' ? 'bg-emerald-50 border-emerald-200'
            : health.overall === 'WARN' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={20} className={
              health.overall === 'OK' ? 'text-emerald-600' : health.overall === 'WARN' ? 'text-amber-600' : 'text-red-600'} />
            <h2 className="text-lg font-black text-slate-800">
              Sistem Durumu: {health.overall === 'OK' ? 'SAĞLIKLI' : health.overall === 'WARN' ? 'UYARI VAR' : 'SORUN VAR'}
            </h2>
            <span className="text-xs text-slate-500 ml-auto">
              Sürüm {health.appVersion} · {health.environment}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(health.checks as HealthCheck[] || []).map((c, i) => (
              <div key={i} className={`border rounded-xl p-3 bg-white/70 ${badge(c.status)}`}>
                <div className="flex items-center gap-2">
                  {c.status === 'OK' ? <CheckCircle size={16} /> : c.status === 'WARN' ? <AlertTriangle size={16} /> : <XCircle size={16} />}
                  <span className="font-black text-sm">{c.name}</span>
                  <span className="ml-auto text-[10px] font-black">{c.status}</span>
                </div>
                <p className="text-xs text-slate-700 mt-1 break-words">{c.detail}</p>
                {c.fix && (
                  <p className="text-[11px] text-slate-600 mt-2 bg-white rounded-lg p-2 border border-slate-200">
                    <b>ÇÖZÜM:</b> {c.fix}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FILTRE */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') load(); }}
            placeholder="Referans no, mesaj veya adres ara..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
          <input type="checkbox" checked={onlyUnresolved} onChange={e => setOnlyUnresolved(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500" />
          Sadece çözülmemiş
        </label>
        <button onClick={load} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700">
          Uygula
        </button>
      </div>

      {/* HATA LISTESI */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 size={24} className="animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <CheckCircle size={36} className="mx-auto opacity-30 mb-2" />
            <p className="text-sm">Kayıtlı hata yok</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map(r => (
              <div key={r.id} className={r.isResolved ? 'opacity-50' : ''}>
                <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3">
                  {expanded === r.id ? <ChevronDown size={16} className="mt-1 text-slate-400" /> : <ChevronRight size={16} className="mt-1 text-slate-400" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-xs font-mono font-black text-slate-800 bg-slate-100 px-2 py-0.5 rounded">{r.referenceNo}</code>
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded ${statusColor(r.statusCode)}`}>
                        {r.statusCode ?? '—'}
                      </span>
                      <span className="text-[10px] font-bold text-slate-500">{r.source}</span>
                      <span className="text-xs text-slate-400 ml-auto">{tarihYaz(r.occurredAt)}</span>
                    </div>
                    <p className="text-sm font-bold text-slate-800 mt-1 truncate">{r.message}</p>
                    <p className="text-xs text-slate-500 font-mono truncate">{r.method} {r.path}{r.queryString}</p>
                    {r.hint && (
                      <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-2 py-1 mt-1">
                        {r.hint}
                      </p>
                    )}
                  </div>
                </button>
                {expanded === r.id && (
                  <div className="px-4 pb-4 space-y-2 bg-slate-50/50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div><span className="text-slate-400">Kullanıcı:</span> <b>{r.userName || '—'}</b></div>
                      <div><span className="text-slate-400">IP:</span> <b>{r.clientIp || '—'}</b></div>
                      <div><span className="text-slate-400">Tip:</span> <b className="break-all">{r.exceptionType || '—'}</b></div>
                      <div><span className="text-slate-400">Tarayıcı:</span> <b className="break-all">{(r.userAgent || '—').substring(0, 40)}</b></div>
                    </div>
                    {r.stackTrace && (
                      <pre className="text-[10px] bg-slate-900 text-slate-200 p-3 rounded-xl overflow-x-auto max-h-80 whitespace-pre-wrap break-all">
                        {r.stackTrace}
                      </pre>
                    )}
                    {!r.isResolved && (
                      <button onClick={() => resolve(r.id)}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700">
                        Çözüldü olarak işaretle
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-center text-xs text-slate-400">
        Kaynak: <code className="px-1 bg-slate-100 rounded font-mono">ErrorLogs</code> tablosu ·
        SERVER = sunucu hatası, CLIENT = tarayıcı hatası
      </p>
    </div>
  );
};

export default SystemErrors;
